import * as yup from 'yup';
import { useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { ControlledField } from '@shared/components/ControlledField';
import { yupResolver } from '@hookform/resolvers/yup';
import { Option } from '@fluentui/react-components';
import { TSparseItem } from '@modules/items';
import { filenameStringSchema } from '@shared/schemas';

// Validation schema for submission form.
const schemaSubmission = yup.object({
  title: filenameStringSchema('Submission title'),
  jurisdiction: yup
    .array()
    .of(yup.string().required('Jurisdiction is required.'))
    .required('Jurisdiction is required.')
    .length(1, 'Jurisdiction is required.'),
  dueDate: yup.string().nullable(),
  leads: yup
    .array()
    .of(yup.string().required())
    .required('Submission leads are required.')
    .min(1, 'At least one lead is required.'),
});

// Infer the form data type from the schema.
export type TFormDataSubmission = yup.InferType<typeof schemaSubmission>;

// Props for the shared form component.
type TProps = {
  initialValues: Partial<TFormDataSubmission>;
  jurisdictions: TSparseItem[];
  onSubmit: (data: TFormDataSubmission) => void;
};

// Default values for the form.
const DEFAULT_VALUES: TFormDataSubmission = { title: '', jurisdiction: [], dueDate: null, leads: [] };

/**
 * SubmissionFormContent
 * ----------------------------
 * A form component that encapsulates all submission fields and validation.
 * It uses forwardRef to expose a `submit` method to its parent.
 */
const SubmissionFormContent = forwardRef<{ submit: () => void }, TProps>(
  ({ initialValues, jurisdictions, onSubmit }, ref) => {
    // Reference for auto-focusing the title input.
    const refSubmissionTitle = useRef<HTMLInputElement>(null);
    const defaultValues = useMemo(() => ({ ...DEFAULT_VALUES, ...initialValues }), [initialValues]);

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<TFormDataSubmission>({
      resolver: yupResolver(schemaSubmission),
      defaultValues,
    });

    // Reset form values and focus the title when the component mounts.
    useEffect(() => {
      reset(defaultValues);
      refSubmissionTitle.current?.focus();
      // We intentionally do not include `defaultValues` in the dependency array to avoid repeated resets.
    }, [refSubmissionTitle.current]);

    // Expose a submit method so the parent can trigger submission.
    useImperativeHandle(ref, () => ({
      submit: handleSubmit(onSubmit),
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col space-y-4">
        {/* Submission Title */}
        <ControlledField
          name="title"
          control={control}
          errors={errors}
          label="Submission title"
          inputType="input"
          placeholder="Enter submission title"
          appearance="underline"
          required
          ref={refSubmissionTitle}
        />

        {/* Jurisdiction Dropdown */}
        <ControlledField
          name="jurisdiction"
          control={control}
          errors={errors}
          label="Jurisdiction"
          inputType="dropdown"
          getLabel={(val) => jurisdictions.find((j) => j._id === val)?.name || ''}
          placeholder="Select jurisdiction"
          required
        >
          {jurisdictions.map((j) => (
            <Option key={j._id} value={j._id}>
              {j.name}
            </Option>
          ))}
        </ControlledField>

        {/* Due Date Field */}
        <ControlledField
          name="dueDate"
          control={control}
          errors={errors}
          label="Due date"
          inputType="date"
          allowTextInput
        />

        {/* Submission Leads */}
        <ControlledField
          name="leads"
          control={control}
          errors={errors}
          label="Submission leads"
          inputType="multiuserselect"
          required
        />
      </form>
    );
  }
);

export default SubmissionFormContent;
